import { hash } from "./rng";
import type { DailyCounters, ProgressState } from "./types";

/** 今日修業 — three deterministic daily quests plus the day-streak (連續修業).
 * Everything derives from the local day key, so the set is stable all day and
 * identical across reloads without any server round trip. */

export type QuestMetric = "correct" | "mastered" | "combo" | "reviews";

export interface QuestDef {
  id: string;
  label: string;
  metric: QuestMetric;
  target: number;
}

export function dayKeyOf(now: number = Date.now()): string {
  const date = new Date(now);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function prevDayKey(dayKey: string): string {
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return "";
  return dayKeyOf(new Date(year, month - 1, day - 1).getTime());
}

export function emptyDaily(dayKey: string = dayKeyOf()): DailyCounters {
  return { dayKey, correct: 0, mastered: 0, bestCombo: 0, reviews: 0 };
}

function pick<T>(values: T[], seed: number, salt: number): T {
  return values[(seed >>> salt) % values.length];
}

/** The three quests for a given day. Targets rotate deterministically so每天
 * 手感略有不同，但永遠可以純靠答題完成（不依賴恰好有到期複習）。 */
export function questsForDay(dayKey: string): QuestDef[] {
  const seed = hash(`lunyu-quests:${dayKey}`);
  const correct = pick([10, 12, 15], seed, 2);
  const mastered = pick([2, 3, 4], seed, 5);
  const combo = pick([4, 5, 6], seed, 8);
  return [
    { id: "correct", label: `破解 ${correct} 題`, metric: "correct", target: correct },
    { id: "mastered", label: `新掌握 ${mastered} 則`, metric: "mastered", target: mastered },
    { id: "combo", label: `連對 ×${combo}`, metric: "combo", target: combo },
  ];
}

export function questValue(daily: DailyCounters, quest: QuestDef): number {
  switch (quest.metric) {
    case "correct":
      return daily.correct;
    case "mastered":
      return daily.mastered;
    case "combo":
      return daily.bestCombo;
    case "reviews":
      return daily.reviews;
    default:
      return 0;
  }
}

export function questDone(daily: DailyCounters, quest: QuestDef): boolean {
  return questValue(daily, quest) >= quest.target;
}

export function questsAllDone(daily: DailyCounters, quests: QuestDef[] = questsForDay(daily.dayKey)): boolean {
  return quests.every((quest) => questDone(daily, quest));
}

export interface QuestStatus {
  id: string;
  label: string;
  have: number;
  need: number;
  done: boolean;
}

/** Today's three quests with their current values — what the UI renders. */
export function questList(daily: DailyCounters): QuestStatus[] {
  return questsForDay(daily.dayKey).map((quest) => ({
    id: quest.id,
    label: quest.label,
    have: Math.min(questValue(daily, quest), quest.target),
    need: quest.target,
    done: questDone(daily, quest),
  }));
}

/** Today's counters — zeroed if the stored ones belong to an earlier day. */
export function dailyFor(state: ProgressState, now: number = Date.now()): DailyCounters {
  const dayKey = dayKeyOf(now);
  return state.daily && state.daily.dayKey === dayKey ? state.daily : emptyDaily(dayKey);
}

/** Day streak as it should be displayed right now: still alive if the last
 * answer was today or yesterday, otherwise broken (0). */
export function currentDayStreak(state: ProgressState, now: number = Date.now()): number {
  const dayKey = dayKeyOf(now);
  if (!state.lastDayKey) return 0;
  if (state.lastDayKey === dayKey || state.lastDayKey === prevDayKey(dayKey)) return state.dayStreak;
  return 0;
}
